import { pickCredential, resolveCredentialState } from '../credentialState';
import { resolveSetupRoute } from '../setupRoute';
import { useLinkedInConfiguration } from './useLinkedInConfiguration';
import { useLinkedInCredentials } from './useLinkedInCredentials';
import { useLinkedInTeam } from './useLinkedInTeam';
import { useStrategyPlan } from './useStrategy';

export interface SetupRouteResult {
  /** True until all three lookups have answered. */
  isLoading: boolean;
  /** The setup step to send the person to, or null when there is none to resume. */
  route: string | null;
}

/**
 * Where a person who opens the LinkedIn Agent overview belongs: the setup step they stopped at, or
 * the overview itself once the agent is set up.
 *
 * <p>Asks the four questions the setup journey is made of: the configuration, the team, the
 * account and the strategy. Each is a query the screens already use, so a person who has just finished a step
 * arrives with the answer one request away and no new endpoint is involved.
 *
 * <p>**A lookup that failed is not an answer.** Sending someone back to the welcome page because a
 * request dropped would trap a company that is fully set up in a journey it has finished. So an
 * error leaves the person where they are, on the overview, whose own requests will show the failure.
 */
export function useSetupRoute(): SetupRouteResult {
  const configuration = useLinkedInConfiguration();
  const team = useLinkedInTeam();
  const credentials = useLinkedInCredentials();
  const strategy = useStrategyPlan();

  if (configuration.isError || team.isError || credentials.isError || strategy.isError) {
    return { isLoading: false, route: null };
  }
  if (configuration.isPending || team.isPending || credentials.isPending || strategy.isPending) {
    return { isLoading: true, route: null };
  }

  return {
    isLoading: false,
    route: resolveSetupRoute({
      configurationStatus: configuration.data?.status ?? null,
      hasTeam: Boolean(team.data?.team),
      credentialState: resolveCredentialState(pickCredential(credentials.data)),
      hasStrategy: strategy.data !== null,
    }),
  };
}
